import * as React from 'react'
import axios from 'axios'

import {
    Box,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    Button,
    Paper,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Divider,
    Link,
} from '@mui/material'

import { UserContext } from '../UserContext'
import { planLabelFor, usesInfranodusLabels } from '../planLabels'
import UsageChip from '../components/Paywall/UsageChip'
import routes from '../routes'
import { usePaywallStore } from '../components/Paywall/usePaywallStore'
import { useBillingConfig } from '../components/Paywall/useChargebeeCheckout'
import {
    deriveSubscriptionState,
    SUBSCRIPTION_STATES,
} from '../components/Paywall/subscriptionState'
import CancelSubscriptionDialog from '../components/Paywall/CancelSubscriptionDialog'
import { openBillingPortal } from '../components/Paywall/openBillingPortal'
import {
    isKeywordGraphApp as getIsKeywordGraphApp,
    appProductName,
    getVariantConfig,
} from '../appVariant'

import { useTheme } from '@mui/material/styles'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

import { sendToAnalytics } from '../Analytics'
import PricingComparisonModal from '../components/PricingComparisonModal'

function Subscription(props) {
    const theme = useTheme()
    const [showCopyFeedback, setShowCopyFeedback] = React.useState(false)
    const [openSnackbar, setOpenSnackbar] = React.useState(false)
    const {
        id,
        username,
        email,
        plan,
        status,
        infranodus_uid,
        tier,
    } = React.useContext(UserContext)
    // Fresh tier after an in-app upgrade, boot tier otherwise.
    const usageTier = usePaywallStore(state => state.usage?.tier || null)
    const isFreeTier = (usageTier || tier) === 'FREE'

    const { billingPortalUrl } = getVariantConfig().brand
    const productName = appProductName()
    const isKeywordGraphApp = getIsKeywordGraphApp()

    const { apiToken, apiTokenExpiryDate } = props

    const convertedApiExpiryDate = convertToDate(apiTokenExpiryDate)

    // If the user has an `infranodus_uid` they're billed through the
    // InfraNodus Chargebee account regardless of which app variant they're
    // currently logged into. Otherwise fall back to the app variant.
    const billsThroughInfranodus = Boolean(infranodus_uid)
    const chargebeeSite = billsThroughInfranodus
        ? 'infranodus'
        : isKeywordGraphApp
        ? 'keywordgraph'
        : 'infranodus'

    const [subscriptionPlan, setSubscriptionPlan] = React.useState('')

    const [isRevealed, setIsRevealed] = React.useState(false)
    const [comparePlansOpen, setComparePlansOpen] = React.useState(false)
    const [cancelOpen, setCancelOpen] = React.useState(false)

    // What Chargebee says right now, not what the boot settings cached: only
    // this can tell a cancellation from a failed renewal, or say when a
    // subscription actually ends.
    const billing = useBillingConfig(true)
    const billingConfig = billing.config
    // Offered in place of cancelling: the step down from what they are on.
    const cheaperPlanLabel = /premium/i.test(subscriptionPlan)
        ? 'Advanced'
        : /pro|advanced/i.test(subscriptionPlan)
        ? 'Basic'
        : null
    const scheduledChange =
        (billingConfig && billingConfig.scheduledChange) || null
    const scheduledPlanLabel = scheduledChange
        ? planLabelFor(
              scheduledChange.planId,
              usesInfranodusLabels(infranodus_uid)
          )
        : null
    // A paying account has no status until Chargebee answers; deriving one
    // earlier would briefly show it as "Free plan".
    const billingPending =
        !isFreeTier && (billing.status === 'idle' || billing.status === 'loading')
    const subscriptionState = billingPending
        ? { kind: null, label: '…', detail: null, endsOn: null }
        : deriveSubscriptionState({
              isFreeTier,
              lookup:
                  billing.status === 'failed'
                      ? 'failed'
                      : billingConfig && billingConfig.subscriptionLookup,
              storedStatus:
                  billingConfig && billingConfig.storedSubscriptionStatus,
              planLabel: subscriptionPlan,
              subscriptionStatus:
                  billingConfig && billingConfig.subscriptionStatus,
              currentTermEnd: billingConfig && billingConfig.currentTermEnd,
              nextBillingAt: billingConfig && billingConfig.nextBillingAt,
              cancelledAt: billingConfig && billingConfig.cancelledAt,
              scheduledChange,
              scheduledPlanLabel,
          })
    const [keepingPlan, setKeepingPlan] = React.useState(false)

    const keepCurrentPlan = React.useCallback(async () => {
        setKeepingPlan(true)
        try {
            await axios.post('/billing/keep-plan', {})
            window.location.reload()
        } catch (error) {
            setKeepingPlan(false)
        }
    }, [])

    const handleToggleReveal = () => {
        setIsRevealed(prev => !prev)
    }

    const handleOpenComparePlans = () => {
        setComparePlansOpen(true)
    }

    const handleCloseComparePlans = () => {
        setComparePlansOpen(false)
    }

    // The plan Chargebee bills right now, so an upgrade shows without logging
    // out: the boot `plan` comes from a settings cache that may be a day old.
    const livePlanId = (billingConfig && billingConfig.currentPlanId) || null
    const planForLabel = livePlanId || (isFreeTier ? 'FREE' : plan)
    React.useEffect(() => {
        setSubscriptionPlan(
            planLabelFor(planForLabel, usesInfranodusLabels(infranodus_uid))
        )
    }, [planForLabel, infranodus_uid])

    // billingConfig may still be loading; fall back to the variant site and the
    // hosted URL. Why the mint site matters: openBillingPortal.js.
    const openSubscriptionPortal = React.useCallback(
        async e => {
            if (e && typeof e.preventDefault === 'function') e.preventDefault()

            sendToAnalytics({
                userId: username,
                event: 'open_subscription_portal',
                data: { source: 'subscription' },
            })
            window.posthog?.capture?.('open_subscription_portal', {
                source: 'subscription',
            })

            await openBillingPortal({
                site: (billingConfig && billingConfig.site) || chargebeeSite,
                fallbackUrl: getVariantConfig().brand.billingPortalUrl,
            })
        },
        [billingConfig, chargebeeSite, username]
    )

    return (
        <>
            <Box
                component="main"
                sx={{
                    backgroundColor: theme => theme.palette.background.default,
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    marginLeft: { xs: 0, md: 4 },
                }}
            >
                <Box
                    sx={{
                        pt: 10,
                        pb: 1,
                        px: { xs: 2, md: 0 },
                        ml: { xs: 'auto', md: 4 },
                        mr: { xs: 'auto', md: 0 },
                        maxWidth: { xs: '600px', md: 1040 },
                    }}
                >
                    {/* Desktop lays the billing section to the right of the
                        table (owner, 2026-09-21); mobile stacks it below. */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 4, md: 8 },
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: '100%', md: 600 },
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Box>
                                <>
                                    <TableContainer
                                        component={Paper}
                                        style={{
                                            boxShadow: 'none',
                                            backgroundColor:
                                                theme.palette.background
                                                    .default,
                                            borderRadius: 0,
                                        }}
                                    >
                                        <Table
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    px: { xs: 1, sm: 2 },
                                                },
                                                // Long usernames/emails have no spaces to
                                                // break on, so on a phone they force the
                                                // table wider than the screen; let them wrap.
                                                '& .MuiTableCell-root .MuiTypography-root, & .MuiTableCell-root a': {
                                                    overflowWrap: 'anywhere',
                                                },
                                                // No divider under the last row: the
                                                // card border already closes the table.
                                                '& tr:last-child .MuiTableCell-root': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            <TableBody>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            Your username:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            {username}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            Your e-mail:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            {email}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            Your current plan:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            {isFreeTier ? (
                                                                <span data-testid="subscription-free-plan">
                                                                    Free plan
                                                                </span>
                                                            ) : (
                                                                <span data-testid="subscription-plan-name">
                                                                    {
                                                                        subscriptionPlan
                                                                    }
                                                                </span>
                                                            )}
                                                        </Typography>
                                                        {/* A free account has the Upgrade button below; one way in is enough. */}
                                                        {isFreeTier ? null : (
                                                            <Link
                                                                variant="body2"
                                                                data-testid="subscription-adjust-plan"
                                                                href={`${routes.upgrade}?return=${routes.subscription}`}
                                                                sx={{
                                                                    display:
                                                                        'inline-block',
                                                                    mt: 1,
                                                                    fontSize:
                                                                        '0.75rem',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .primary
                                                                            .main,
                                                                    cursor:
                                                                        'pointer',
                                                                    textDecoration:
                                                                        'none',
                                                                    border:
                                                                        '1px solid',
                                                                    borderColor:
                                                                        theme
                                                                            .palette
                                                                            .primary
                                                                            .main,
                                                                    borderRadius:
                                                                        '4px',
                                                                    padding:
                                                                        '4px 10px',
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .primary
                                                                                .main,
                                                                        color:
                                                                            '#fff',
                                                                    },
                                                                }}
                                                            >
                                                                Adjust plan
                                                            </Link>
                                                        )}
                                                        {isFreeTier ? (
                                                            <Box
                                                                sx={{
                                                                    mt: 1.5,
                                                                    display:
                                                                        'flex',
                                                                    flexDirection:
                                                                        'column',
                                                                    alignItems:
                                                                        'flex-end',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <UsageChip placement="subscription" />
                                                                <Link
                                                                    variant="body2"
                                                                    data-testid="subscription-upgrade"
                                                                    href={`${routes.upgrade}?return=${routes.subscription}`}
                                                                    sx={{
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        color:
                                                                            '#fff',
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .primary
                                                                                .main,
                                                                        cursor:
                                                                            'pointer',
                                                                        textDecoration:
                                                                            'none',
                                                                        border:
                                                                            '1px solid',
                                                                        borderColor:
                                                                            theme
                                                                                .palette
                                                                                .primary
                                                                                .main,
                                                                        borderRadius:
                                                                            '4px',
                                                                        padding:
                                                                            '4px 10px',
                                                                    }}
                                                                >
                                                                    Upgrade
                                                                </Link>
                                                            </Box>
                                                        ) : null}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            Your subscription
                                                            status:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            <span data-testid="subscription-status">
                                                                {
                                                                    subscriptionState.label
                                                                }
                                                            </span>
                                                        </Typography>
                                                        {subscriptionState.kind ===
                                                        SUBSCRIPTION_STATES.DOWNGRADING ? (
                                                            <Box
                                                                data-testid="subscription-downgrade-notice"
                                                                sx={{ mt: 1 }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{
                                                                        color:
                                                                            theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                        display:
                                                                            'block',
                                                                    }}
                                                                >
                                                                    Your plan
                                                                    will be
                                                                    downgraded
                                                                    to{' '}
                                                                    {
                                                                        scheduledPlanLabel
                                                                    }{' '}
                                                                    on your next
                                                                    billing
                                                                    cycle
                                                                    {subscriptionState.endsOn
                                                                        ? ` (${subscriptionState.endsOn})`
                                                                        : ''}
                                                                    . Switching
                                                                    to{' '}
                                                                    {
                                                                        scheduledPlanLabel
                                                                    }{' '}
                                                                    means you'll
                                                                    have less
                                                                    usage, lose
                                                                    early access
                                                                    to new
                                                                    features,
                                                                    and won't
                                                                    get priority
                                                                    at busy
                                                                    times.
                                                                </Typography>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    disabled={
                                                                        keepingPlan
                                                                    }
                                                                    onClick={
                                                                        keepCurrentPlan
                                                                    }
                                                                    data-testid="subscription-keep-plan"
                                                                    sx={{
                                                                        mt: 1,
                                                                        textTransform:
                                                                            'none',
                                                                    }}
                                                                >
                                                                    {keepingPlan
                                                                        ? 'Keeping…'
                                                                        : `Keep your ${subscriptionPlan}`}
                                                                </Button>
                                                            </Box>
                                                        ) : subscriptionState.detail ? (
                                                            <Typography
                                                                variant="caption"
                                                                data-testid="subscription-status-detail"
                                                                style={{
                                                                    color:
                                                                        subscriptionState.kind ===
                                                                            SUBSCRIPTION_STATES.PAYMENT_FAILED ||
                                                                        subscriptionState.kind ===
                                                                            SUBSCRIPTION_STATES.UNAVAILABLE
                                                                            ? theme
                                                                                  .palette
                                                                                  .error
                                                                                  .main
                                                                            : theme
                                                                                  .palette
                                                                                  .text
                                                                                  .secondary,
                                                                    display:
                                                                        'block',
                                                                    marginTop: 4,
                                                                }}
                                                            >
                                                                {
                                                                    subscriptionState.detail
                                                                }
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            Subscription
                                                            provided by:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            <a
                                                                href="/about/our-company"
                                                                target="_blank"
                                                            >
                                                                {chargebeeSite ===
                                                                'keywordgraph'
                                                                    ? 'KeywordGraph / Nodus Labs'
                                                                    : 'InfraNodus SAS'}
                                                            </a>
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow
                                                    style={{
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        <Typography variant="body1">
                                                            API Access:
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                            }}
                                                        >
                                                            <a
                                                                href="/api-access"
                                                                data-testid="subscription-api-access"
                                                            >
                                                                generate API
                                                                keys
                                                            </a>
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                {!isFreeTier &&
                                                subscriptionState.kind !==
                                                    SUBSCRIPTION_STATES.ENDING ? (
                                                    <TableRow
                                                        style={{
                                                            borderBottom:
                                                                'none',
                                                        }}
                                                    >
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                        >
                                                            <Typography variant="body1">
                                                                Cancel plan:
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Link
                                                                component="button"
                                                                variant="body2"
                                                                data-testid="subscription-cancel"
                                                                onClick={() =>
                                                                    setCancelOpen(
                                                                        true
                                                                    )
                                                                }
                                                                sx={{
                                                                    display:
                                                                        'inline-block',
                                                                    fontSize:
                                                                        '0.75rem',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .error
                                                                            .main,
                                                                    cursor:
                                                                        'pointer',
                                                                    textDecoration:
                                                                        'none',
                                                                    border:
                                                                        '1px solid',
                                                                    borderColor:
                                                                        theme
                                                                            .palette
                                                                            .error
                                                                            .main,
                                                                    borderRadius:
                                                                        '4px',
                                                                    padding:
                                                                        '4px 10px',
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .error
                                                                                .main,
                                                                        color:
                                                                            '#fff',
                                                                    },
                                                                }}
                                                            >
                                                                Cancel
                                                                subscription
                                                            </Link>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : null}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            </Box>
                        </Box>
                        {/* Only when a Chargebee customer exists: without one
                            the portal errors with "Session expired". */}
                        {billingConfig && billingConfig.hasCustomer ? (
                            <Box
                                sx={{
                                    width: { xs: '100%', md: 340 },
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 3,
                                }}
                            >
                            <Typography variant="h6" sx={{ mt: 0, mb: 2 }}>
                                Invoices and payment details
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                • download your invoices <br />
                                • change your card or payment method <br />
                                • update your billing address
                                <br />
                                <br />
                                These are handled by Chargebee, our payment
                                provider. You are signed in automatically. If
                                not, contact our customer support!
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={openSubscriptionPortal}
                                data-testid="subscription-billing-button"
                                sx={{
                                    fontSize: '15px',
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                }}
                            >
                                Open invoices and payment details
                            </Button>
                            </Box>
                        ) : null}
                    </Box>
                    <Box sx={{ mb: 8, mt: 4, maxWidth: '600px' }}>
                        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                            Problems with Your Subscription?
                        </Typography>
                        <Typography variant="body1">
                            If you have any questions about your subscription,
                            cancellations or upgrades, please, check our{' '}
                            <a href="https://support.noduslabs.com/hc/en-us/categories/360002513599-Subscriptions-and-Payments">
                                Support Portal
                            </a>{' '}
                            and feel free to{' '}
                            <a href="https://support.noduslabs.com/hc/en-us/requests/new">
                                contact us
                            </a>{' '}
                            directly.
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    API key copied into your clipboard. Use Ctrl/Cmd + V to
                    paste it into your app.
                </Alert>
            </Snackbar>
            <PricingComparisonModal
                open={comparePlansOpen}
                onClose={handleCloseComparePlans}
            />
            <CancelSubscriptionDialog
                open={cancelOpen}
                planLabel={subscriptionPlan}
                endsOn={subscriptionState.endsOn}
                cheaperPlanLabel={cheaperPlanLabel}
                onClose={() => setCancelOpen(false)}
                onCancelled={() => window.location.reload()}
            />
        </>
    )
}

export default Subscription

function convertToDate(dateStr) {
    try {
        const date = new Date(dateStr)

        if (isNaN(date.getTime())) {
            return 'N/A'
        }

        // Format the date to 'dd month yyyy, hh:mm'
        const day = date
            .getDate()
            .toString()
            .padStart(2, '0')
        const month = date.toLocaleString('default', { month: 'long' })
        const year = date.getFullYear()
        const hours = date
            .getHours()
            .toString()
            .padStart(2, '0')
        const minutes = date
            .getMinutes()
            .toString()
            .padStart(2, '0')

        return `${day} ${month} ${year}, ${hours}:${minutes}`
    } catch (error) {
        console.error('Error converting date:', error.message)
        return 'N/A'
    }
}
